// @flow
import {
  isMinor as checkIsMinor,
  getIsStudent,
  isPrimaryMember,
  getAdultMembers,
} from 'pages/AffordaHousehold/utils';
import { ADDITIONAL_MINORS_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.AdditionalMinors.constants';
import { handleRequestAndThrowIfError } from 'utils/api';
import type { PrimaryFormPayload } from 'hooks/mutations/useSavePrimaryFormMutation.utils';

export const getIds = ({
  affordableRelationships,
  applicantTypes,
  member,
  relationships,
}) => {
  const applicantTypeId = applicantTypes.find(
    ({ name }) => name === member.applicantType,
  )?.id;
  const affordableRelationshipId = affordableRelationships.find(
    ({ name }) => name === member.affordableRelationship,
  )?.id;
  const relationshipId = relationships.find(
    ({ name }) => name === member.relationship,
  )?.id;

  return {
    applicantType: undefined,
    type: applicantTypeId,
    affordableRelationship: affordableRelationshipId,
    relationship: relationshipId,
    relationshipId,
    affordableRelationshipId,
  };
};

const getSpouse = (member, members) => {
  const spouse = members.find(
    ({ whoIsYourSpouse }) => whoIsYourSpouse === member.id,
  );

  if (spouse) {
    return {
      firstName: spouse?.firstName,
      lastName: spouse?.lastName,
    };
  } else if (member.spousesFirstName?.length && member.spousesSurname?.length) {
    // Use the new separate fields
    return {
      firstName: member.spousesFirstName,
      lastName: member.spousesSurname,
    };
  } else if (member.spousesFullName?.length) {
    // Fallback for backward compatibility
    const spouseFullNameParts = member.spousesFullName.split(' ');
    const length = spouseFullNameParts.length;

    if (length === 2) {
      return {
        firstName: spouseFullNameParts[0],
        lastName: spouseFullNameParts[1],
      };
    } else if (length === 3) {
      return {
        firstName: spouseFullNameParts[0],
        middleName: spouseFullNameParts[1],
        lastName: spouseFullNameParts[2],
      };
    }
  }

  return {};
};
export const updateForms = async ({
  additionalMinors,
  affordableRelationships,
  applicantId,
  applicantTypes,
  member,
  members,
  minors,
  relationships,
  savePrimaryFormMutation,
  saveSecondaryFormMutation,
  studentStatus,
}) => {
  const adultMembers = getAdultMembers(members);
  const isMinor = checkIsMinor(member);
  const isPrimaryUser = isPrimaryMember(member);
  const additionalAdults = adultMembers?.filter(
    (adult) => adult?.id !== member?.id,
  )?.length;

  const formPayload: PrimaryFormPayload = {
    applicantId,
    basicInformation: {
      firstName: member.firstName,
      lastName: member.lastName,
      birthday: new Date(member.birthday),
    },
    contactInformation: {
      phoneNumber: member.phoneNumber,
      emailAddress: member.emailAddress,
    },
    maritalInformation: {
      maritalStatus: member.maritalStatus,
      ...getSpouse(member, adultMembers),
    },
    additionalOccupants: {
      adults: {
        anyAdult: additionalAdults > 0 ? 'yes' : 'no',
        howManyAdults: additionalAdults,
      },
      minors: {
        anyMinor: minors?.length > 0 ? 'yes' : 'no',
      },
    },
    studentInformation: {
      isStudent: getIsStudent(member),
      studentType: member.studentStatus,
      TANFAssistance: studentStatus?.TANFAssistance,
      jobTrainingProgram: studentStatus?.jobTrainingProgram,
      singleParentDependantChild: studentStatus?.singleParentDependantChild,
      marriedJointReturn: studentStatus?.marriedJointReturn,
      fosterProgram: studentStatus?.fosterProgram,
    },
  };

  if (isPrimaryUser) {
    formPayload.additionalOccupants = {
      ...formPayload.additionalOccupants,
      anticipateChangeHousehold: additionalMinors?.[
        ADDITIONAL_MINORS_FIELD_NAMES.expectingMinorsType
      ].includes(ADDITIONAL_MINORS_FIELD_NAMES.notApplicable)
        ? 'no'
        : 'yes',
      anticipateChangeHouseholdAdoption:
        additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.adoptionCount],
      anticipateChangeHouseholdFoster:
        additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.guardianshipCount],
      anticipateChangeHouseholdPregnancy:
        additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.pregnancyCount],
      minors: {
        ...formPayload.additionalOccupants.minors,
        minorsInformation: minors.map((minor) => {
          const { affordableRelationship, relationship } = getIds({
            affordableRelationships,
            applicantTypes,
            member: minor,
            relationships,
          });

          return {
            firstName: minor.firstName,
            lastName: minor.lastName,
            studentType: minor.studentStatus,
            dateOfBirth: new Date(minor.birthday),
            affordableRelationship,
            relationship,
            jointCustody: minor.jointCustody,
            legallyResponsible: minor.legallyResponsible,
          };
        }),
      },
    };

    return handleRequestAndThrowIfError(
      savePrimaryFormMutation.mutateAsync(formPayload),
    );
  } else if (!isMinor) {
    return handleRequestAndThrowIfError(
      saveSecondaryFormMutation.mutateAsync(formPayload),
    );
  }
};

export const getPrimaryOrSecondaryForm = ({
  additionalMinors,
  affordableRelationships,
  applicantTypes,
  member,
  members,
  relationships,
  studentStatus,
}) => {
  const isMinor = checkIsMinor(member);
  const minors = members.filter((member) => checkIsMinor(member));
  if (isMinor) {
    return { formPayload: null };
  }

  const adultMembers = getAdultMembers(members);
  const isPrimaryUser = isPrimaryMember(member);
  const additionalAdults = adultMembers?.filter(
    (adult) => adult?.id !== member?.id,
  )?.length;

  const formPayload: PrimaryFormPayload = {
    // applicantId, This one is set in the backend after the member is created
    basicInformation: {
      firstName: member.firstName,
      lastName: member.lastName,
      birthday: new Date(member.birthday),
    },
    contactInformation: {
      phoneNumber: member.phoneNumber,
      emailAddress: member.emailAddress,
    },
    maritalInformation: {
      maritalStatus: member.maritalStatus,
      ...getSpouse(member, adultMembers),
    },
    additionalOccupants: {
      adults: {
        anyAdult: additionalAdults > 0 ? 'yes' : 'no',
        howManyAdults: additionalAdults,
      },
      minors: {
        anyMinor: minors?.length > 0 ? 'yes' : 'no',
      },
    },
    studentInformation: {
      isStudent: getIsStudent(member),
      studentType: member.studentStatus,
      TANFAssistance: studentStatus?.TANFAssistance,
      jobTrainingProgram: studentStatus?.jobTrainingProgram,
      singleParentDependantChild: studentStatus?.singleParentDependantChild,
      marriedJointReturn: studentStatus?.marriedJointReturn,
      fosterProgram: studentStatus?.fosterProgram,
    },
  };

  if (isPrimaryUser) {
    formPayload.additionalOccupants = {
      ...formPayload.additionalOccupants,
      anticipateChangeHousehold: additionalMinors?.[
        ADDITIONAL_MINORS_FIELD_NAMES.expectingMinorsType
      ].includes(ADDITIONAL_MINORS_FIELD_NAMES.notApplicable)
        ? 'no'
        : 'yes',
      anticipateChangeHouseholdAdoption:
        additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.adoptionCount],
      anticipateChangeHouseholdFoster:
        additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.guardianshipCount],
      anticipateChangeHouseholdPregnancy:
        additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.pregnancyCount],
      minors: {
        ...formPayload.additionalOccupants.minors,
        minorsInformation: minors.map((minor) => {
          const { affordableRelationship, relationship } = getIds({
            affordableRelationships,
            applicantTypes,
            member: minor,
            relationships,
          });

          return {
            firstName: minor.firstName,
            lastName: minor.lastName,
            studentType: minor.studentStatus,
            dateOfBirth: new Date(minor.birthday),
            affordableRelationship,
            relationship,
            jointCustody: minor.jointCustody,
            legallyResponsible: minor.legallyResponsible,
          };
        }),
      },
    };
  }
  return {
    formPayload,
  };
};
